import axios from 'axios';

// const OLLAMA_URL = (process.env.REACT_APP_OLLAMA_URL || 'https://ollama-xc4g8cog8kgo80kkwkos8gkc.whattodo.tech').replace(/\/$/, '');
const OLLAMA_URL = (process.env.REACT_APP_OLLAMA_URL || 'https://ollama.clicklaw.tech').replace(/\/$/, '');
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'gemma3:latest';
// Ollama: /api/chat. Open WebUI (OpenAI-compatible): /api/chat/completions
const OLLAMA_CHAT_PATH = process.env.REACT_APP_OLLAMA_CHAT_PATH || '/api/chat/completions';

function getOllamaApiKey(): string | undefined {
  const v = process.env.REACT_APP_OLLAMA_API_KEY;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function unauthorizedMessage(): string {
  const hasKey = Boolean(getOllamaApiKey());
  if (!hasKey) {
    return 'Unauthorized. Missing REACT_APP_OLLAMA_API_KEY (if you just edited frontend/.env, restart the dev server).';
  }
  return 'Unauthorized. Your REACT_APP_OLLAMA_API_KEY was sent but rejected by the server (check the key and which OLLAMA_URL you are calling).';
}

const ollamaClient = axios.create({
  baseURL: OLLAMA_URL,
  headers: { 'Content-Type': 'application/json' },
});

export type OllamaMessageRole = 'system' | 'user' | 'assistant';
export interface OllamaMessage {
  role: OllamaMessageRole;
  content: string;
}

/** Ollama: { message: { content } }. Open WebUI / OpenAI: { choices: [{ message: { content } }] } */
function parseChatContent(data: unknown, path: string): string {
  const d = data as Record<string, unknown>;
  if (path.includes('completions') && Array.isArray(d?.choices) && d.choices.length > 0) {
    const msg = (d.choices[0] as Record<string, unknown>)?.message as Record<string, unknown> | undefined;
    return (msg?.content as string) ?? '';
  }
  const msg = d?.message as Record<string, unknown> | undefined;
  return (msg?.content as string) ?? '';
}

function buildSendMessages(
  messages: { role: string; content: string }[],
  context?: string
): { role: string; content: string }[] {
  const isCompletions = OLLAMA_CHAT_PATH.includes('completions');
  let send: { role: string; content: string }[] = isCompletions
    ? messages.filter((m) => m.role !== 'system')
    : [...messages];

  if (context && context.trim()) {
    const block = `Context:\n${context.trim()}\n\n`;
    if (isCompletions) {
      // Prepend context to the *last* user message so the model sees document + latest question together
      const lastUserIdx = send.map((m) => m.role).lastIndexOf('user');
      if (lastUserIdx >= 0) {
        send = [...send];
        send[lastUserIdx] = { ...send[lastUserIdx], content: block + send[lastUserIdx].content };
      }
    } else {
      const i = send.findIndex((m) => m.role === 'system');
      if (i >= 0) {
        send = [...send];
        send[i] = { ...send[i], content: block + (send[i].content || '') };
      } else {
        send = [{ role: 'system', content: block.trim() }, ...send];
      }
    }
  }
  return send;
}

/**
 * Send a chat request to Ollama /api/chat or Open WebUI /api/chat/completions.
 * @param messages - Full conversation including optional system prompt
 * @param context - Optional context (product, page, user) injected before the model sees the chat.
 * @returns The assistant's reply content
 */
export async function sendOllamaChat(
  messages: { role: string; content: string }[],
  context?: string
): Promise<string> {
  const sendMessages = buildSendMessages(messages, context);
  const body = { model: OLLAMA_MODEL, messages: sendMessages, stream: false };
  const headers: Record<string, string> = {};
  const apiKey = getOllamaApiKey();
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;

  try {
    const { data } = await ollamaClient.post(OLLAMA_CHAT_PATH, body, { headers });
    return parseChatContent(data, OLLAMA_CHAT_PATH);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const res = err.response?.data as Record<string, unknown> | undefined;
      const msg = res?.error ?? res?.detail ?? res?.message ?? err.message;
      if (status === 404) throw new Error('Model or endpoint not found. Check OLLAMA_URL, OLLAMA_CHAT_PATH, and OLLAMA_MODEL.');
      if (status === 401) throw new Error(unauthorizedMessage());
      if (status === 405) throw new Error('Method not allowed. For Open WebUI use REACT_APP_OLLAMA_CHAT_PATH=/api/chat/completions');
      if (status === 400) throw new Error(`Bad request (400): ${String(msg)}. If it persists, try REACT_APP_OLLAMA_MODEL=llama3.2 or a model your instance supports.`);
      if (status && status >= 400) throw new Error(String(msg || `Request failed (${status}).`));
      throw new Error(err.message || 'Network error. Is the server reachable?');
    }
    throw err instanceof Error ? err : new Error('Failed to get a response from the chat.');
  }
}

/**
 * Stream a chat request; onToken is called with each incremental piece (token) of the reply.
 * Supports Ollama /api/chat (NDJSON) and Open WebUI /api/chat/completions (SSE).
 */
export async function sendOllamaChatStream(
  messages: { role: string; content: string }[],
  context: string | undefined,
  onToken: (text: string) => void
): Promise<void> {
  const sendMessages = buildSendMessages(messages, context);
  const body = { model: OLLAMA_MODEL, messages: sendMessages, stream: true };
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const apiKey = getOllamaApiKey();
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;

  const url = `${OLLAMA_URL}${OLLAMA_CHAT_PATH}`;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const msg = err?.error ?? err?.detail ?? err?.message ?? res.statusText;
    if (res.status === 401) throw new Error(unauthorizedMessage());
    if (res.status === 400) throw new Error(`Bad request: ${String(msg)}`);
    throw new Error(String(msg || `Request failed (${res.status}).`));
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Streaming not supported');

  const dec = new TextDecoder();
  let buf = '';
  const isCompletions = OLLAMA_CHAT_PATH.includes('completions');

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });

    if (isCompletions) {
      // SSE: data: {"choices":[{"delta":{"content":"..."}}]}\n or data: [DONE]
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;
        try {
          const obj = JSON.parse(data) as Record<string, unknown>;
          const choices = obj?.choices as Array<Record<string, unknown>> | undefined;
          const delta = choices?.[0]?.delta as Record<string, unknown> | undefined;
          const c = delta?.content as string | undefined;
          if (typeof c === 'string' && c) onToken(c);
        } catch {
          // ignore parse errors for malformed chunks
        }
      }
    } else {
      // Ollama NDJSON: one JSON object per line, message.content is the delta
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        const t = line.trim();
        if (!t) continue;
        try {
          const obj = JSON.parse(t) as Record<string, unknown>;
          const msg = obj?.message as Record<string, unknown> | undefined;
          const c = msg?.content as string | undefined;
          if (typeof c === 'string' && c) onToken(c);
        } catch {
          // ignore
        }
      }
    }
  }
}
